import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  updateSearchQuery,
  setIngredientError,
  openPopupAction,
  closePopupAction,
} from "../../redux/actions/actions";
import { startRecipeSearchThunk } from "../../redux/thunks/search_recipe_thunk";
import {
  selectIngredients,
  selectIngredientErrors,
  selectSearchQuery,
  selectLoading,
  selectRecipes,
} from "../../redux/selectors/selectors";
import { errorMessages, inputGuides } from "../../helpers/helper_objects";
import {
  Typography,
  TextField,
  Button,
  Dialog,
  DialogContent,
  CircularProgress,
  Box,
} from "@mui/material";
import { resetIngredientsErrors } from "../../helpers/helper_functions";

const SearchBox = () => {
  const dispatch = useDispatch();
  const ingredientsList = useSelector(selectIngredients);
  const searchQuery = useSelector(selectSearchQuery);
  const ingredientErrors = useSelector(selectIngredientErrors);
  const openPopup = useSelector((state) => state.popup.open);
  const loading = useSelector(selectLoading);
  const loaded = useSelector((state) => state.loaded);
  const error = useSelector((state) => state.error);
  const recipes = useSelector(selectRecipes);
  const recipesAvailable = recipes.length > 0;
  const noHits = !recipesAvailable && !loading;

  const handleSearchQueryChange = (e) => {
    dispatch(setIngredientError("invalidKeyword", false));
    dispatch(setIngredientError("emptyQuery", false));
    const newKeyWord = e.target.value.trimStart();
    dispatch(updateSearchQuery(newKeyWord));
  };

  const handleFormSubmit = () => {
    const isSearchQueryValid =
      typeof searchQuery === "string" && /^[a-zA-Z ]+$/.test(searchQuery);

    resetIngredientsErrors(dispatch, setIngredientError, false);
    dispatch(
      setIngredientError("emptyIngredients", ingredientsList.length === 0),
    );
    dispatch(setIngredientError("emptyQuery", searchQuery.length === 0));
    dispatch(setIngredientError("invalidKeyword", !isSearchQueryValid));

    if (searchQuery && ingredientsList.length > 0 && isSearchQueryValid) {
      resetIngredientsErrors(dispatch, setIngredientError, false);
      dispatch(startRecipeSearchThunk());
    }
  };

  useEffect(() => {
    if (noHits && !loading && loaded > 0) dispatch(openPopupAction());
  }, [noHits, loading, loaded, dispatch]);

  const handleClosePopup = () => dispatch(closePopupAction());

  return (
    <Box data-testid="search-box-component" sx={{ width: "100%" }}>
      <TextField
        id="searchInput"
        type="text"
        fullWidth
        value={searchQuery}
        onChange={handleSearchQueryChange}
        label={inputGuides.keywordInput}
        error={ingredientErrors.invalidKeyword || ingredientErrors.emptyQuery}
        helperText={
          ingredientErrors.emptyQuery
            ? errorMessages.noKeyword
            : ingredientErrors.invalidKeyword
              ? errorMessages.invalidKeyword
              : ""
        }
        onFocus={() => {
          dispatch(setIngredientError("emptyQuery", false));
          dispatch(setIngredientError("invalidKeyword", false));
        }}
        InputLabelProps={{
          sx: {
            color: "", // default label
            "&.Mui-focused": {
              color: "", // default label
              "&.Mui-focused": { color: "green" }, // focused label
              "&.Mui-error": { color: "#d32f2f" },
            }, // focused label
            "&.Mui-error": { color: "#d32f2f" },
          },
        }}
        FormHelperTextProps={{ sx: { color: "#ffb74d" } }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 3,
            background: "white",
            color: "black",
            "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
            "&:hover fieldset": { borderColor: "rgba(255,255,255,0.24)" },
            "&.Mui-focused fieldset": { borderColor: "#ffb74d" },
          },
        }}
      />

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }} data-testid="loading-spinner">
          <CircularProgress />
        </Box>
      ) : (
        <Button
          type="submit"
          onClick={handleFormSubmit}
          variant="contained"
          sx={{
            mt: 2,
            width: "100%",
            borderRadius: 3,
            py: 1.2,
            fontWeight: 900,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "black",
            background: "linear-gradient(90deg, #ffb74d, #ffa726)",
            boxShadow: "0 14px 30px rgba(0,0,0,0.35)",
            "&:hover": {
              background: "linear-gradient(90deg, #ffc266, #ffb74d)",
              boxShadow: "0 18px 40px rgba(0,0,0,0.5)",
              transform: "translateY(-2px)",
            },
            transition: "transform 160ms ease, box-shadow 160ms ease",
          }}
        >
          Search
        </Button>
      )}

      {/* No results dialog */}
      {!noHits || openPopup ? (
        <Dialog
          open={openPopup}
          onClose={handleClosePopup}
          data-testid="dialog-popup"
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: "hidden",
              background:
                "linear-gradient(145deg, rgba(20,20,20,0.95), rgba(10,10,10,0.95))",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "white",
              boxShadow: "0 22px 70px rgba(0,0,0,0.65)",
              minWidth: { xs: "88vw", sm: 420 },
            },
          }}
        >
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
              No results
            </Typography>

            <Typography sx={{ color: "rgba(255,255,255,0.75)" }}>
              {!error ? errorMessages.noResults : error}
            </Typography>

            <Button
              onClick={handleClosePopup}
              variant="contained"
              sx={{
                mt: 2.5,
                borderRadius: 3,
                fontWeight: 900,
                color: "black",
                background: "linear-gradient(90deg, #ffb74d, #ffa726)",
                "&:hover": {
                  background: "linear-gradient(90deg, #ffc266, #ffb74d)",
                },
              }}
            >
              Close
            </Button>
          </DialogContent>
        </Dialog>
      ) : null}
    </Box>
  );
};

export default SearchBox;
